import { Component, Input, OnChanges, SimpleChanges, ViewChild, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';

/** Basic address check: one '@', a dot in the domain, no spaces or separators. */
const EMAIL_PATTERN = /^[^\s@<>()\[\],;:"]+@[^\s@<>()\[\],;:"]+\.[^\s@<>()\[\],;:"]{2,}$/;

export function isValidEmail(value: string): boolean {
  return EMAIL_PATTERN.test((value || '').trim());
}

/** Splits "a@x.com; b@y.com, c@z.com" into a de-duplicated list (case-insensitive). */
export function splitEmailList(value: string | string[] | null | undefined): string[] {
  const parts = Array.isArray(value) ? value : (value || '').split(/[;,\s]+/);
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of parts) {
    const email = (raw || '').trim();
    if (!email) { continue; }
    const key = email.toLowerCase();
    if (seen.has(key)) { continue; }
    seen.add(key);
    out.push(email);
  }
  return out;
}

/**
 * Multiple email addresses as chips (ng-select with tags).
 *
 * Value: one string, addresses separated by ';' (what the notification worker
 * expects in NTo / NCc / NBcc), e.g. "a@x.com;b@y.com". Accepts ';' or ','
 * separated strings, or a string[], on write.
 *
 * Typing ',', ';' or a space, pressing Enter/Tab, or leaving the field adds
 * the typed address. Pasting a list adds every valid address in it. Invalid
 * addresses are not added; a message lists them under the field.
 *
 * Used by the Print page Email dialog and by UIC forms (uiInputType 'multiemail').
 */
@Component({
  selector: 'app-multi-email-input',
  templateUrl: './multi-email-input.component.html',
  styleUrls: ['./multi-email-input.component.css'],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => MultiEmailInputComponent), multi: true }
  ]
})
export class MultiEmailInputComponent implements ControlValueAccessor, OnChanges {
  @Input() placeholder: string | null | undefined = 'Type an email and press Enter';
  /** Addresses offered in the dropdown, e.g. the customer's contacts. */
  @Input() suggestions: string[] = [];
  /** Shows the chips without letting the user change them. */
  @Input() readonly = false;
  @Input() inputId: string | null = null;
  @Input() appendTo = 'body';

  @ViewChild(NgSelectComponent) select?: NgSelectComponent;

  emails: string[] = [];
  disabled = false;
  invalidText = '';

  private onChange: (value: string) => void = () => { };
  private onTouched: () => void = () => { };

  /** Dropdown items: suggestions plus the chosen addresses (kept as a field so ng-select is not re-fed every change detection). */
  items: string[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['suggestions']) {
      this.refreshItems();
    }
  }

  /** ng-select [addTag] (Enter / Tab): one new valid address is returned to ng-select; anything else is handled here. */
  addTag = (term: string): string | null => {
    const parts = splitEmailList(term);
    if (parts.length === 1 && isValidEmail(parts[0]) && !this.contains(parts[0])) {
      this.invalidText = '';
      return parts[0];
    }
    this.addFromText(term);
    return null;
  };

  /** Typing ',', ';' or a space after an address adds it. */
  onSearch(event: { term: string }): void {
    const term = event?.term || '';
    if (/[;,\s]$/.test(term)) {
      this.addFromText(term);
    }
  }

  /** Leaving the field adds what was typed. */
  onBlur(): void {
    const term = this.select?.searchTerm;
    if (term && term.trim()) {
      this.addFromText(term);
    }
    this.onTouched();
  }

  /** Adds every valid address in the text (typed or pasted list) and reports the invalid ones. */
  addFromText(text: string): void {
    const parts = splitEmailList(text);
    const invalid = parts.filter(p => !isValidEmail(p));
    const fresh = parts.filter(p => isValidEmail(p) && !this.contains(p));
    this.invalidText = invalid.length ? `Not a valid email: ${invalid.join(', ')}` : '';
    if (fresh.length) {
      this.setEmails([...this.emails, ...fresh]);
    }
    this.clearSearch();
  }

  onModelChange(value: string[] | null): void {
    this.setEmails(value || []);
  }

  // ControlValueAccessor
  writeValue(value: string | string[] | null): void {
    this.emails = splitEmailList(value);
    this.invalidText = '';
    this.refreshItems();
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  private setEmails(list: string[]): void {
    this.emails = splitEmailList(list);
    this.refreshItems();
    this.onChange(this.emails.join(';'));
  }

  private refreshItems(): void {
    this.items = splitEmailList([...(this.suggestions || []), ...this.emails]);
  }

  private contains(email: string): boolean {
    return this.emails.some(e => e.toLowerCase() === email.toLowerCase());
  }

  /**
   * Clears the typed text and closes the dropdown after an address is added. ng-select is
   * OnPush, so the input is redrawn with detectChanges(); otherwise the typed text and the
   * "Add ..." dropdown stay on screen and the open dropdown covers the fields below it.
   */
  private clearSearch(): void {
    setTimeout(() => {
      if (this.select) {
        this.select.searchTerm = null as unknown as string;
        this.select.close();
        this.select.detectChanges();
      }
    });
  }
}
